import { Permissions as P, type PermissionName } from '@comms/shared';

export const PERMISSION_GROUPS: {
  title: string;
  items: { key: PermissionName; label: string; desc: string }[];
}[] = [
  {
    title: 'General',
    items: [
      {
        key: 'VIEW_CHANNEL',
        label: 'View Channels',
        desc: 'See channels and read their messages.',
      },
      {
        key: 'MANAGE_CHANNELS',
        label: 'Manage Channels',
        desc: 'Create, edit and delete channels.',
      },
      {
        key: 'MANAGE_ROLES',
        label: 'Manage Roles',
        desc: 'Create and edit roles below their highest role.',
      },
      {
        key: 'MANAGE_GUILD',
        label: 'Manage Server',
        desc: 'Change the server name, icon and settings.',
      },
      {
        key: 'VIEW_AUDIT_LOG',
        label: 'View Audit Log',
        desc: 'See a record of moderation actions.',
      },
      {
        key: 'MANAGE_EMOJIS',
        label: 'Manage Emoji & Stickers',
        desc: 'Upload, rename and remove custom emoji and stickers.',
      },
      {
        key: 'MANAGE_WEBHOOKS',
        label: 'Manage Webhooks',
        desc: 'Create, reset and delete webhooks that post into channels.',
      },
    ],
  },
  {
    title: 'Membership',
    items: [
      { key: 'CREATE_INVITE', label: 'Create Invite', desc: 'Invite people to this server.' },
      { key: 'CHANGE_NICKNAME', label: 'Change Nickname', desc: 'Change their own nickname.' },
      {
        key: 'MANAGE_NICKNAMES',
        label: 'Manage Nicknames',
        desc: "Change other members' nicknames.",
      },
      {
        key: 'KICK_MEMBERS',
        label: 'Kick Members',
        desc: 'Remove members (they can rejoin with an invite).',
      },
      { key: 'BAN_MEMBERS', label: 'Ban Members', desc: 'Permanently remove members.' },
      {
        key: 'MODERATE_MEMBERS',
        label: 'Timeout Members',
        desc: 'Temporarily stop members from chatting.',
      },
    ],
  },
  {
    title: 'Text',
    items: [
      { key: 'SEND_MESSAGES', label: 'Send Messages', desc: 'Send messages in text channels.' },
      {
        key: 'READ_MESSAGE_HISTORY',
        label: 'Read Message History',
        desc: 'Read messages sent before they arrived.',
      },
      { key: 'EMBED_LINKS', label: 'Embed Links', desc: 'Links show previews.' },
      { key: 'ATTACH_FILES', label: 'Attach Files', desc: 'Upload files and images.' },
      { key: 'ADD_REACTIONS', label: 'Add Reactions', desc: 'Add new emoji reactions.' },
      {
        key: 'USE_EXTERNAL_EMOJIS',
        label: 'Use External Emoji',
        desc: 'Use emoji from other servers.',
      },
      {
        key: 'MENTION_EVERYONE',
        label: 'Mention @everyone and all roles',
        desc: 'Ping everyone or any role.',
      },
      { key: 'MANAGE_MESSAGES', label: 'Manage Messages', desc: "Delete others' messages." },
      { key: 'PIN_MESSAGES', label: 'Pin Messages', desc: 'Pin and unpin messages.' },
      { key: 'SEND_POLLS', label: 'Create Polls', desc: 'Post polls.' },
      {
        key: 'USE_APPLICATION_COMMANDS',
        label: 'Use Application Commands',
        desc: "Run bots' slash commands.",
      },
      {
        key: 'CREATE_PUBLIC_THREADS',
        label: 'Create Public Threads',
        desc: 'Start threads everyone in the channel can see.',
      },
      {
        key: 'CREATE_PRIVATE_THREADS',
        label: 'Create Private Threads',
        desc: 'Start invite-only threads.',
      },
      {
        key: 'SEND_MESSAGES_IN_THREADS',
        label: 'Send Messages in Threads',
        desc: 'Reply in threads and forum posts.',
      },
      {
        key: 'MANAGE_THREADS',
        label: 'Manage Threads',
        desc: 'Rename, archive, lock and delete any thread; see private threads.',
      },
    ],
  },
  {
    title: 'Voice',
    items: [
      { key: 'CONNECT', label: 'Connect', desc: 'Join voice channels.' },
      { key: 'SPEAK', label: 'Speak', desc: 'Talk in voice channels.' },
      { key: 'STREAM', label: 'Video & Screen Share', desc: 'Share camera or screen.' },
      { key: 'MUTE_MEMBERS', label: 'Mute Members', desc: 'Mute others in voice.' },
      { key: 'DEAFEN_MEMBERS', label: 'Deafen Members', desc: 'Deafen others in voice.' },
      { key: 'MOVE_MEMBERS', label: 'Move Members', desc: 'Move members between voice channels.' },
      { key: 'PRIORITY_SPEAKER', label: 'Priority Speaker', desc: 'Be heard more easily.' },
      { key: 'USE_VAD', label: 'Use Voice Activity', desc: 'Talk without push-to-talk.' },
    ],
  },
  {
    title: 'Events',
    items: [
      { key: 'CREATE_EVENTS', label: 'Create Events', desc: 'Schedule events and edit their own.' },
      { key: 'MANAGE_EVENTS', label: 'Manage Events', desc: "Edit and cancel anyone's events." },
    ],
  },
  {
    title: 'Advanced',
    items: [
      {
        key: 'ADMINISTRATOR',
        label: 'Administrator',
        desc: 'Every permission, bypassing channel overrides. Dangerous!',
      },
    ],
  },
];

export const CHANNEL_PERMISSION_KEYS = new Set<PermissionName>([
  'VIEW_CHANNEL',
  'MANAGE_CHANNELS',
  'MANAGE_ROLES',
  'CREATE_INVITE',
  'SEND_MESSAGES',
  'READ_MESSAGE_HISTORY',
  'EMBED_LINKS',
  'ATTACH_FILES',
  'ADD_REACTIONS',
  'MENTION_EVERYONE',
  'MANAGE_MESSAGES',
  'PIN_MESSAGES',
  'USE_EXTERNAL_EMOJIS',
  'SEND_POLLS',
  'USE_APPLICATION_COMMANDS',
  'CREATE_PUBLIC_THREADS',
  'CREATE_PRIVATE_THREADS',
  'SEND_MESSAGES_IN_THREADS',
  'MANAGE_THREADS',
  'MANAGE_WEBHOOKS',
  'CONNECT',
  'SPEAK',
  'STREAM',
]);

export { P };
